/**
 * Tenant Content Tab
 * Manage tenant-private guides, library resources, and agent templates
 */

import { useState } from "react";
import {
  useTenantGuides,
  useTenantLibraryResources,
  useTenantAgentTemplates,
  useDeleteTenantGuide,
  useDeleteTenantLibraryResource,
  useDeleteTenantAgentTemplate,
} from "@hooks/useTenantAdmin";
import type {
  TenantGuideListItem,
  TenantLibraryResourceListItem,
  TenantAgentTemplateListItem,
} from "@/types/tenantAdmin";
import { cn } from "@utils/cn";

// Content type tabs
type ContentType = "guides" | "library" | "templates";

// Content type configuration
const contentTypes: {
  id: ContentType;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    id: "guides",
    label: "Guides",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    id: "library",
    label: "Library Resources",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  },
  {
    id: "templates",
    label: "Agent Templates",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
        />
      </svg>
    ),
  },
];

// Empty state component
interface EmptyStateProps {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="text-center py-12">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <h3 className="mt-4 text-sm font-medium text-gray-900 dark:text-white">
        {title}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {description}
      </p>
      {action && (
        <div className="mt-6">
          <button
            onClick={action.onClick}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {action.label}
          </button>
        </div>
      )}
    </div>
  );
}

// List item component for guides
interface GuideListItemProps {
  guide: TenantGuideListItem;
  onDelete: (slug: string) => void;
  deleting?: boolean;
}

function GuideListItemRow({ guide, onDelete, deleting }: GuideListItemProps) {
  return (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {guide.title}
          </h4>
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
              guide.published
                ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
            )}
          >
            {guide.published ? "Published" : "Draft"}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
          {guide.description}
        </p>
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          Slug: {guide.slug}
        </p>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={() => onDelete(guide.slug)}
          disabled={deleting}
          className={cn(
            "p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors",
            deleting && "opacity-50 cursor-not-allowed",
          )}
          title="Delete"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// List item component for library resources
interface LibraryListItemProps {
  resource: TenantLibraryResourceListItem;
  onDelete: (id: string) => void;
  deleting?: boolean;
}

function LibraryListItemRow({
  resource,
  onDelete,
  deleting,
}: LibraryListItemProps) {
  const typeLabels: Record<string, string> = {
    article: "Article",
    video: "Video",
    pdf: "PDF",
    tool: "Tool",
    course: "Course",
    documentation: "Documentation",
    blog: "Blog",
  };

  const difficultyColors: Record<string, string> = {
    beginner:
      "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    intermediate:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    advanced: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
  };

  return (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {resource.title}
          </h4>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {typeLabels[resource.resourceType] || resource.resourceType}
          </span>
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
              difficultyColors[resource.difficulty],
            )}
          >
            {resource.difficulty}
          </span>
          {resource.featured && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400">
              Featured
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
          {resource.description}
        </p>
        {resource.topics.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {resource.topics.map((topic) => (
              <span
                key={topic}
                className="text-xs text-gray-400 dark:text-gray-500"
              >
                #{topic}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={() => onDelete(resource.id)}
          disabled={deleting}
          className={cn(
            "p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors",
            deleting && "opacity-50 cursor-not-allowed",
          )}
          title="Delete"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// List item component for templates
interface TemplateListItemProps {
  template: TenantAgentTemplateListItem;
  onDelete: (id: string) => void;
  deleting?: boolean;
}

function TemplateListItemRow({
  template,
  onDelete,
  deleting,
}: TemplateListItemProps) {
  const categoryLabels: Record<string, string> = {
    hr: "HR",
    scheduling: "Scheduling",
    faq: "FAQ",
    "customer-service": "Customer Service",
    "data-entry": "Data Entry",
    workflow: "Workflow",
    healthcare: "Healthcare",
    other: "Other",
  };

  const difficultyColors: Record<string, string> = {
    beginner:
      "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
    intermediate:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400",
    advanced: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
  };

  return (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {template.name}
          </h4>
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
            {categoryLabels[template.category] || template.category}
          </span>
          <span
            className={cn(
              "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
              difficultyColors[template.difficulty],
            )}
          >
            {template.difficulty}
          </span>
          {template.featured && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400">
              Featured
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">
          {template.description}
        </p>
        <div className="mt-1 flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
          <span>By {template.authorName}</span>
          <span>{template.downloadCount} downloads</span>
          {template.hasTools && <span>Has tools</span>}
          {template.hasSubAgents && <span>Has sub-agents</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 ml-4">
        <button
          onClick={() => onDelete(template.id)}
          disabled={deleting}
          className={cn(
            "p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors",
            deleting && "opacity-50 cursor-not-allowed",
          )}
          title="Delete"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Guides list component
function GuidesList() {
  const { data: guides, isLoading, error } = useTenantGuides();
  const deleteGuide = useDeleteTenantGuide();

  const handleDelete = async (slug: string) => {
    if (confirm("Are you sure you want to delete this guide?")) {
      await deleteGuide.mutateAsync(slug);
    }
  };

  if (error) {
    return (
      <div className="text-center py-8 text-red-600 dark:text-red-400">
        Failed to load guides
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!guides || guides.length === 0) {
    return (
      <EmptyState
        title="No guides yet"
        description="Create private guides for your organization"
        action={{
          label: "Create Guide",
          onClick: () => {
            // TODO: Open create guide modal
            alert("Create guide functionality coming soon");
          },
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {guides.map((guide) => (
        <GuideListItemRow
          key={guide.id}
          guide={guide}
          onDelete={handleDelete}
          deleting={deleteGuide.isPending}
        />
      ))}
    </div>
  );
}

// Library resources list component
function LibraryList() {
  const { data: resources, isLoading, error } = useTenantLibraryResources();
  const deleteResource = useDeleteTenantLibraryResource();

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this resource?")) {
      await deleteResource.mutateAsync(id);
    }
  };

  if (error) {
    return (
      <div className="text-center py-8 text-red-600 dark:text-red-400">
        Failed to load library resources
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!resources || resources.length === 0) {
    return (
      <EmptyState
        title="No library resources yet"
        description="Add private learning resources for your organization"
        action={{
          label: "Add Resource",
          onClick: () => {
            // TODO: Open create resource modal
            alert("Add resource functionality coming soon");
          },
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {resources.map((resource) => (
        <LibraryListItemRow
          key={resource.id}
          resource={resource}
          onDelete={handleDelete}
          deleting={deleteResource.isPending}
        />
      ))}
    </div>
  );
}

// Templates list component
function TemplatesList() {
  const { data: templates, isLoading, error } = useTenantAgentTemplates();
  const deleteTemplate = useDeleteTenantAgentTemplate();

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this template?")) {
      await deleteTemplate.mutateAsync(id);
    }
  };

  if (error) {
    return (
      <div className="text-center py-8 text-red-600 dark:text-red-400">
        Failed to load agent templates
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <EmptyState
        title="No agent templates yet"
        description="Create private agent templates for your organization"
        action={{
          label: "Create Template",
          onClick: () => {
            // TODO: Open create template modal
            alert("Create template functionality coming soon");
          },
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {templates.map((template) => (
        <TemplateListItemRow
          key={template.id}
          template={template}
          onDelete={handleDelete}
          deleting={deleteTemplate.isPending}
        />
      ))}
    </div>
  );
}

export function TenantContentTab() {
  const [activeType, setActiveType] = useState<ContentType>("guides");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            My Organization Content
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage private content visible only to your organization
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
            Private
          </span>
        </div>
      </div>

      {/* Content type tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-4" aria-label="Content type tabs">
          {contentTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => setActiveType(type.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors",
                activeType === type.id
                  ? "border-primary-500 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600",
              )}
            >
              {type.icon}
              {type.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content list */}
      <div>
        {activeType === "guides" && <GuidesList />}
        {activeType === "library" && <LibraryList />}
        {activeType === "templates" && <TemplatesList />}
      </div>
    </div>
  );
}
